// The words athletes accept (fresh-start spec §3), shared by Register's one
// acceptance block and the "We've updated our terms" modal (TermsModal.jsx):
//   - the AI coaching consent (ai_coaching v1);
//   - the Strava data retention (strava_data_retention v1), as a summary, and
//     in full (STRAVA_TERMS) where Strava is connected;
//   - the Terms and Privacy links, and the one agreement.
// Changing a consent's words materially means a new consent version: bump it
// in the backend's routes_consents.CURRENT_VERSIONS (the apps read the
// versions from /consents/status). (Not a substitute for the full ToS: legal
// should review this wording and the linked Terms.) Mobile mirrors it in
// lib/ai-consent.ts (AI_CONSENT_PARAGRAPHS, AI_CONSENT_AGREEMENT).
import React from 'react';
import { PRIVACY_URL, TERMS_URL } from '../../lib/aiConsent';
import { CheckRow } from '../appstate/ModalShell';

export function AiConsentText() {
  return (
    <>
      <p className="m-0 type-body-small text-text-body">
        Your training plans, workouts, and coaching advice in Aithlo are
        generated by AI. AI is{" "}
        <span className="font-medium">non-deterministic</span> — we can't
        predict or guarantee what it produces, and it can occasionally be
        wrong or suggest something that isn't right for you. Aithlo
        personalises using your workout and training history and the details
        you share, but it doesn't know your full medical picture.
      </p>
      <p className="m-0 type-body-small text-text-body">
        Aithlo is not a medical device and does not provide medical advice.
        Before you start any plan or session, it's your responsibility to
        decide whether it's safe and appropriate for you, and to check with a
        doctor — especially if you have any injury, health condition, or
        symptoms. Stop and seek medical help if you ever feel unwell, dizzy,
        or in pain. You train at your own risk.
      </p>
    </>
  );
}

/** The Strava data terms in full: the connect dialogs and the terms modal list them. */
export const STRAVA_TERMS = [
  "Your activities will be retained in Aithlo while Strava is connected — not just for 7 days.",
  "When you edit or delete an activity in Strava, we update or delete our copy within 48 hours via Strava's webhook.",
  "If you disconnect Strava from Aithlo, or revoke Aithlo at strava.com, every Strava activity we hold about you is deleted.",
  "When an activity matches a planned session, Aithlo may add a small \"📋\" marker and a \"planned by Aithlo.com\" note to its title and description on Strava. This is on by default — you can turn it off anytime in Settings → Data.",
  "Your Strava data is only shown to you (and to a coach you have explicitly accepted). It is never sold or shared with advertisers.",
];

/** The Strava retention in one paragraph (sign-up). */
export function StravaRetentionSummary() {
  return (
    <p className="m-0 type-body-small text-text-body">
      If you connect Strava, Aithlo keeps a copy of your activities for as long
      as it's connected, updates or deletes it within 48 hours when you change
      them on Strava, and deletes all of it when you disconnect. Only you and a
      coach you accept see it; it's never sold or shared with advertisers.
    </p>
  );
}

const LINK = 'text-accent-default underline hover:text-accent-hover';
export const TermsLink = ({ children = 'Terms of Service' }) => (
  <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={LINK}>{children}</a>
);
export const PrivacyLink = ({ children = 'Privacy Policy' }) => (
  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={LINK}>{children}</a>
);

/**
 * The one agreement. `consents`: it covers the consent words shown above it
 * (and the Terms and Privacy Policy); `adult`: it confirms 18+ as well. Only
 * `adult`: the 18+ confirmation on its own.
 */
export function AcceptCheckbox({ id = 'terms-accept', checked, onChange, disabled = false, consents = true, adult = true }) {
  return (
    <CheckRow id={id} checked={checked} onChange={onChange} disabled={disabled}>
      {consents ? (
        <>
          I understand Aithlo's coaching is AI-generated and not medical
          advice, I agree to the above and to the <TermsLink /> and{' '}
          <PrivacyLink />{adult ? ", and I'm 18 or over." : '.'}
        </>
      ) : (
        <>I'm 18 or over. <span className="text-text-secondary">Aithlo coaching is for adults.</span></>
      )}
    </CheckRow>
  );
}
